import { useMemo, useState } from 'react'
import { isEqual } from 'lodash-es'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { ISelectOption } from '$/types'
import { useAppSelector } from 'app/hooks'

import CreateScenarioWrapper, {
  ScenarioCreationWrapperType
} from './CreateScenarioWrapper'
import EditScenarioWrapper, {
  ScenarioEditWrapperType
} from './EditScenarioWrapper'
import { scenarioToRuleWithId } from './scenarioToRuleWithId'
import {
  IQRAScenario,
  IRuleWithId,
  ScenarioOptions,
  WISViewMode
} from './types'
import { selectScenario } from './whatIfScenarioSelectors'

export type ScenarioWrapperType = {
  scenarioOptions: ScenarioOptions
  setViewMode: React.Dispatch<React.SetStateAction<WISViewMode>>
  viewMode: WISViewMode
  isModalOpen: boolean
  closeModal: (pathnameOverride?: string) => void
}

const EMPTY_THREAT = {
  label: '',
  value: ''
}

const hasNegativeValue = (value: number | boolean | string): boolean => {
  return Number(value) < 0
}

const ScenarioWrapper = ({
  viewMode,
  setViewMode,
  scenarioOptions,
  closeModal,
  isModalOpen
}: ScenarioWrapperType) => {
  const scenario = useAppSelector(selectScenario)

  const [scenarioName, setScenarioName] = useState<string>(scenario.name)
  const [scenarioRules, setScenarioRules] = useState<IRuleWithId[]>(
    scenarioToRuleWithId(scenario.rules)
  )

  const [selectedThreat, setSelectedThreat] =
    useState<ISelectOption>(EMPTY_THREAT)

  const isScenarioRulesValid = useMemo(() => {
    // at least one rule needs to have a condition with before and after values
    const rulesWithValidConditions = scenarioRules.filter(
      (rule) =>
        rule.conditions.length &&
        rule.conditions.every(
          (condition) =>
            condition.beforeValue !== '' && condition.afterValue !== ''
        )
    )

    if (!rulesWithValidConditions.length) {
      return false
    }

    // no duplicate conditions allowed
    for (
      let ruleIndex = 0;
      ruleIndex < rulesWithValidConditions.length;
      ruleIndex++
    ) {
      for (
        let conditionIndex = 0;
        conditionIndex < scenarioRules[ruleIndex].conditions.length;
        conditionIndex++
      ) {
        const nextConditionIndex = conditionIndex + 1
        const condition = scenarioRules[ruleIndex].conditions[conditionIndex]
        const nextCondition =
          scenarioRules[ruleIndex].conditions[nextConditionIndex]

        // we are at the final condition and we have no duplicates
        if (!nextCondition) {
          return true
        }

        // break out of the loop if a duplicate condition is found
        if (isEqual(condition, nextCondition)) {
          return false
        }
      }
    }

    return false
  }, [scenarioRules])

  const hasAnyNegativeRuleValue = useMemo(
    () =>
      scenarioRules.some((rule) =>
        rule.conditions.some(
          (condition) =>
            hasNegativeValue(condition.beforeValue) ||
            hasNegativeValue(condition.afterValue)
        )
      ),
    [scenarioRules]
  )

  const handleCancel = () => {
    setViewMode('run')
    setScenarioRules([])
    setScenarioName('')
    setSelectedThreat(EMPTY_THREAT)
  }

  const cancelButton = (
    <button
      type='button'
      className='align-middle text-lighthouseBlue'
      onClick={handleCancel}
    >
      <ArrowBackIosIcon fontSize='small' />
      <span className='align-middle'>Back</span>
    </button>
  )

  const applyToScenario = (
    applyScenarioCallback: (scenario: IQRAScenario) => void
  ) => {
    const validRules = scenarioRules.filter(
      (rule) =>
        rule.attributeCode !== '' &&
        rule.attributeName !== '' &&
        rule.attributeType !== '' &&
        rule.conditions.length > 0
    )

    const newScenario = {
      id: scenario.id,
      name: scenarioName,
      rules: validRules
    }

    applyScenarioCallback(newScenario)
  }

  const canConfirm =
    isScenarioRulesValid && scenarioName.length > 0 && !hasAnyNegativeRuleValue

  const WISScenarioWrapper: Record<
    string,
    React.FC<ScenarioCreationWrapperType> | React.FC<ScenarioEditWrapperType>
  > = {
    create: CreateScenarioWrapper,
    edit: EditScenarioWrapper
  }

  const ScenarioWrapper = WISScenarioWrapper[viewMode]

  return (
    <ScenarioWrapper
      canConfirm={canConfirm}
      setViewMode={setViewMode}
      scenarioOptions={scenarioOptions}
      closeModal={closeModal}
      isModalOpen={isModalOpen}
      cancelButton={cancelButton}
      applyToScenario={applyToScenario}
      selectedThreat={selectedThreat}
      scenarioName={scenarioName}
      scenarioRules={scenarioRules}
      setScenarioRules={setScenarioRules}
      setSelectedThreat={setSelectedThreat}
      setScenarioName={setScenarioName}
    />
  )
}

export default ScenarioWrapper
